import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import QRCode from 'qrcode'
import { useSelector } from 'react-redux'
import { formatToFourDigits } from 'utils'
import { getContrastColor } from 'utils/colors'
import {
  adjustFontSizeToFitWidthPx,
  buildBadgeQrContent,
  ptToPx,
  ptToPxNumber,
  truncateTextToWidthPx,
  wrapTextToWidthPx
} from 'utils/badges'
import { storageRef } from '../../../../index'
import { Collection } from 'constants/index'

const BadgePreview = ({ badgeConfig, orientation = 'portrait', previewAs, subject }) => {
  const classes = useStyles()
  const [qrCode, setQrCode] = useState(null)
  const { conference } = useSelector((state) => state.conference)
  const [bannerURL, setBannerURL] = useState(null)
  const [bannerError, setBannerError] = useState(false)

  const fetchBanner = async () => {
    try {
      const banner = await storageRef
        .child(`${conference?.id}/${Collection.CONFERENCES}/${conference?.confLogo}`)
        .getDownloadURL()
      setBannerURL(banner)
      setBannerError(false)
    } catch (err) {
      setBannerURL(null)
      setBannerError(true)
    }
  }

  useEffect(() => {
    if (conference?.confLogo) {
      fetchBanner()
    } else {
      setBannerURL(null)
      setBannerError(false)
    }
  }, [conference?.confLogo])

  const fallbackData = {
    firstName: 'JOHN',
    lastName: 'DOE',
    credential: 'MD, FACS',
    position: 'Research Analyst',
    organization: 'Nexora Solutions',
    city: 'New York',
    state: 'NA',
    country: 'United States',
    email: 'john.doe@example.com',
    phone: '+1 555-123-4567',
    counter: 3001,
    type: previewAs || badgeConfig?.applicableSubjects?.[0] || 'Attendee'
  }
  const sampleData = subject
    ? { ...subject, type: previewAs || subject.type || fallbackData.type }
    : fallbackData

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const fieldsToEncodeInQRCode = badgeConfig?.fieldsAppliedInsideQRCode || []
        const badgeNameFormat = badgeConfig?.badgeNameFormat || 'firstName lastName'

        const dataToEncodeInQRCode = {}
        fieldsToEncodeInQRCode.forEach((field) => {
          dataToEncodeInQRCode[field] = sampleData[field]
        })

        const resultToBeEncoded = buildBadgeQrContent(
          dataToEncodeInQRCode,
          fieldsToEncodeInQRCode,
          badgeNameFormat,
          conference?.badgeQrCodeFormat
        )

        const qrCodeUrl = await QRCode.toDataURL(resultToBeEncoded ? resultToBeEncoded : ' ', {
          margin: 2,
          width: 400,
          errorCorrectionLevel: 'L'
        })
        setQrCode(qrCodeUrl)
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    }

    generateQRCode()
  }, [badgeConfig, subject, previewAs])

  const isLandscape = orientation === 'landscape'
  const badgeClassName = isLandscape ? classes.badgeLandscape : classes.badgePortrait
  const contentClassName = isLandscape ? classes.badgeContentLandscape : classes.badgeContent

  const badgeBarColor = badgeConfig?.badgeBarColor || '#000000'
  const textColor = getContrastColor(badgeBarColor)

  const rawLayout = badgeConfig?.badgeLayout
  const badgeLayout = Array.isArray(rawLayout)
    ? rawLayout
    : rawLayout && typeof rawLayout === 'object'
    ? Array.from({ length: 5 }, (_, i) => rawLayout[String(i)] || [])
    : []
  const badgeNameFormat = badgeConfig?.badgeNameFormat || 'firstName lastName'
  const badgeLineFontSizes = badgeConfig?.badgeLineFontSizes || [22, 12, 12, 12, 12]
  const badgeContentAlignment = badgeConfig?.badgeContentAlignment || 'center'
  const alignItems = badgeContentAlignment === 'left' ? 'flex-start' : 'center'
  const textAlign = badgeContentAlignment === 'left' ? 'left' : 'center'

  // Render text for a single layout line (lines 2-5), preserving the configured field order
  const renderLineText = (fieldKeys) => {
    return fieldKeys
      .map((key) => sampleData[key])
      .filter(Boolean)
      .join(' · ')
  }

  // Build Line 1 name string based on format (no credential — it belongs in badgeLayout lines 2-5)
  const hasName = sampleData.firstName || sampleData.lastName
  const line1Text = hasName
    ? badgeNameFormat === 'lastName, firstName'
      ? `${sampleData.lastName}, ${sampleData.firstName}`
      : `${sampleData.firstName} ${sampleData.lastName}`
    : ''

  // Available text width mirrors the PDF's availableWidth/lineWidth: badge content box width,
  // minus left padding when left-aligned, minus the QR code column when landscape.
  const badgeBoxWidthPx = isLandscape ? 400 : 288
  const badgePaddingPx = isLandscape ? 7 : 8
  const leftPaddingPx = badgeContentAlignment === 'left' ? 10 : 0
  const qrColumnWidthPx = 86
  const contentWidthPx = badgeBoxWidthPx - badgePaddingPx * 2 - leftPaddingPx
  const isLeftAligned = badgeContentAlignment === 'left'
  // Left-aligned text starts flush left, so it only needs to clear the QR column on the right.
  // Center-aligned text extends symmetrically from the midpoint, so the QR column must be
  // excluded from both sides to keep the text from overlapping it.
  const availableWidthPx = isLandscape
    ? isLeftAligned
      ? contentWidthPx - qrColumnWidthPx
      : contentWidthPx - qrColumnWidthPx * 2.05
    : contentWidthPx

  // The name is rendered uppercase (textTransform), so measure and wrap the uppercase form.
  const line1Upper = (line1Text || '').toUpperCase()
  const NAME_FONT_OPTS = { fontWeight: 'bold' }
  const line1FontSize = adjustFontSizeToFitWidthPx(
    line1Upper || ' ',
    ptToPxNumber(badgeLineFontSizes[0] ?? 22),
    contentWidthPx,
    { ...NAME_FONT_OPTS, minSizePx: 6 }
  )
  // Mirrors the PDF: the font size above bottoms out at a floor, so a name too long to fit even
  // there (e.g. a 50-char first and last name) has to break across lines instead of overflowing
  // a nowrap line that `overflow: hidden` would then clip at both ends.
  const line1Lines = line1Upper
    ? wrapTextToWidthPx(line1Upper, line1FontSize, contentWidthPx, NAME_FONT_OPTS).map((line) =>
        truncateTextToWidthPx(line, line1FontSize, contentWidthPx, NAME_FONT_OPTS)
      )
    : []

  const roleText = (badgeConfig?.subjectLabels?.[sampleData.type] || sampleData.type || 'Attendee')
    .slice(0, 50)
    .toUpperCase()
  const footerBoxWidthPx = badgeBoxWidthPx - badgePaddingPx * 2
  const footerLabelFontSize = adjustFontSizeToFitWidthPx(
    roleText,
    ptToPxNumber(badgeConfig?.badgeLabelFontSize ?? 30),
    footerBoxWidthPx - 16,
    { fontWeight: 'bold' }
  )

  // Precompute each rendered body line's width-adjusted font size and its wrapped physical lines
  // up front (instead of inline in JSX) so we can size the vertical budget below before
  // rendering. Wrapping is measured rather than left to CSS so the budget knows the real line
  // count, and so an unbreakable token gets the same ellipsis treatment as in the PDF.
  const bodyLines = badgeLayout.slice(1).reduce((acc, fieldKeys, idx) => {
    if (!fieldKeys || fieldKeys.length === 0) return acc
    const text = renderLineText(fieldKeys)
    if (!text) return acc
    const lineIndex = idx + 1
    const fontSizePx = adjustFontSizeToFitWidthPx(
      text,
      ptToPxNumber(badgeLineFontSizes[lineIndex] ?? 12),
      availableWidthPx
    )
    const lines = wrapTextToWidthPx(text, fontSizePx, availableWidthPx).map((line) =>
      truncateTextToWidthPx(line, fontSizePx, availableWidthPx)
    )
    return [...acc, { key: idx, lines, fontSizePx }]
  }, [])

  // Font sizes are user-configured, so instead of overriding them when a combination doesn't
  // fit, we first tighten the shared line-height (normally 1.15) down to a single-spaced floor
  // of 1.0. This mirrors the fixed geometry of the card: header/footer are constant pixel
  // heights, so the leftover room for name + body lines can be computed up front rather than
  // measured from the live DOM. If even single-spaced text doesn't fit (e.g. the name at its
  // 30pt cap plus four body lines all at their 20pt cap), we claw back the rest from the QR
  // thumbnail as a last resort — this only affects the preview's thumbnail size, not the
  // printed badge's actual QR dimensions.
  const IDEAL_LINE_HEIGHT = 1.15
  const MIN_LINE_HEIGHT = 1.0
  const IDEAL_QR_SIZE_PX = 86
  const MIN_QR_SIZE_PX = 64
  const cardHeightPx = isLandscape ? 300 : 380
  const headerHeightPx = 86.4
  const footerHeightPx = 44
  const counterHeightPx = ptToPxNumber(10) * IDEAL_LINE_HEIGHT
  // In landscape the QR sits in an absolutely-positioned column beside the text, so it doesn't
  // eat into the text lines' vertical budget the way it does stacked below them in portrait.
  const availableTextHeightPx = (qrSizePx) =>
    cardHeightPx -
    badgePaddingPx * 2 -
    headerHeightPx -
    footerHeightPx -
    (isLandscape ? 0 : 2 /* marginTop */ + qrSizePx + counterHeightPx)

  // One entry per *physical* line, so a wrapped name or body line is budgeted for its real height.
  const allLineFontSizes = [
    ...line1Lines.map(() => line1FontSize),
    ...bodyLines.flatMap((l) => l.lines.map(() => l.fontSizePx))
  ]
  const rawTextHeightPx = allLineFontSizes.reduce((sum, fs) => sum + fs, 0)

  let qrSizePx = IDEAL_QR_SIZE_PX
  let lineHeight = IDEAL_LINE_HEIGHT
  const idealBudgetPx = availableTextHeightPx(qrSizePx)
  if (rawTextHeightPx > 0 && rawTextHeightPx * IDEAL_LINE_HEIGHT > idealBudgetPx) {
    lineHeight = Math.max(MIN_LINE_HEIGHT, idealBudgetPx / rawTextHeightPx)
    if (!isLandscape && rawTextHeightPx * MIN_LINE_HEIGHT > idealBudgetPx) {
      const deficitPx = rawTextHeightPx * MIN_LINE_HEIGHT - idealBudgetPx
      qrSizePx = Math.max(MIN_QR_SIZE_PX, IDEAL_QR_SIZE_PX - deficitPx)
      lineHeight = MIN_LINE_HEIGHT
    }
  }

  return (
    <Box className={classes.previewContainer}>
      <Paper className={badgeClassName} elevation={3}>
        {/* Header */}
        {bannerURL ? (
          <Box sx={{ width: '100%', height: '86.4px', mb: '0px' }}>
            <img src={bannerURL} alt="Badge Banner" width="100%" height="100%" />
          </Box>
        ) : conference?.confLogo && !bannerError ? (
          <Box
            sx={{
              width: '100%',
              height: '86.4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: '0px'
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '86.4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: '0px'
            }}>
            <Typography sx={{ color: 'red', fontSize: '13px', fontWeight: 'bold' }}>
              {bannerError ? 'Failed to load badge banner' : 'Badge banner not uploaded'}
            </Typography>
          </Box>
        )}

        <Box className={contentClassName}>
          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems
            }}>
            {/* Line 1 — always name, format-driven. Rendered one <p> per wrapped physical line;
                each already fits contentWidthPx, so nowrap keeps CSS from re-breaking it
                somewhere the measured vertical budget above didn't account for. */}
            {line1Lines.map((text, index) => (
              <p
                key={`name-${index}`}
                className={classes.lastName}
                style={{
                  fontSize: `${line1FontSize}px`,
                  lineHeight,
                  margin: '0px',
                  textAlign,
                  whiteSpace: 'nowrap'
                }}>
                {text}
              </p>
            ))}

            {/* Lines 2-5 — driven by badgeLayout */}
            {bodyLines.map(({ key, lines, fontSizePx }) =>
              lines.map((text, index) => (
                <p
                  key={`${key}-${index}`}
                  className={classes.bodyLine}
                  style={{
                    fontSize: `${fontSizePx}px`,
                    lineHeight,
                    textAlign,
                    whiteSpace: 'nowrap'
                  }}>
                  {text}
                </p>
              ))
            )}

            {/* QR code */}
            {qrCode && (
              <Box
                className={classes.qrCodeLandscape}
                sx={
                  isLandscape
                    ? {
                        position: 'absolute',
                        right: '0px',
                        // Clears the footer bar, which is pinned to the card's true
                        // bottom edge and ignores this box's paddingBottom.
                        bottom: '48px'
                      }
                    : {
                        textAlign: 'center',
                        marginTop: '2px',
                        width: '100%',
                        alignItems: 'center'
                      }
                }>
                {isLandscape && (
                  <p className={classes.badgeCounter}>{formatToFourDigits(sampleData.counter)}</p>
                )}
                <img src={qrCode} alt="QR Code" width={qrSizePx} height={qrSizePx} />
                {!isLandscape && (
                  <p className={classes.badgeCounter}>{formatToFourDigits(sampleData.counter)}</p>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.badgeFooter} sx={{ backgroundColor: badgeBarColor }}>
          <Typography
            style={{
              color: textColor,
              fontSize: `${footerLabelFontSize}px`,
              fontWeight: 'bold'
            }}>
            {roleText}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default BadgePreview

const useStyles = makeStyles(() => ({
  previewContainer: {
    fontFamily: 'helvetica, arial, sans-serif',
    marginTop: '24px',
    width: '100%'
  },
  badgePortrait: {
    width: '288px',
    height: '380px',
    position: 'relative',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '8px'
  },
  badgeLandscape: {
    width: '400px',
    height: '300px',
    position: 'relative',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    padding: '7px'
  },
  badgeHeader: {
    width: '100%',
    height: '100px',
    backgroundColor: '#0F0F64',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px'
  },
  conferenceTitle: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  badgeContent: {
    // padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    // Reserves room for the footer, which is pinned via position:absolute and no
    // longer takes up space in this flex column.
    paddingBottom: '44px',
    overflow: 'hidden'
  },
  badgeContentLandscape: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexGrow: 1,
    position: 'relative',
    paddingBottom: '44px',
    overflow: 'hidden'
  },
  firstName: {
    fontFamily: 'helvetica, arial, sans-serif',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  lastName: {
    fontFamily: 'helvetica, arial, sans-serif',
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase'
    // lineHeight is set inline — see the budget-based calculation in the component body.
  },
  bodyLine: {
    fontSize: ptToPx(12),
    fontFamily: 'helvetica, arial, sans-serif',
    fontWeight: 'normal',
    textAlign: 'center',
    margin: '0px'
    // lineHeight is set inline — see the budget-based calculation in the component body.
  },
  badgeFooter: {
    width: '100%',
    height: '44px',
    // Pinned to the card's true bottom edge so it never gets pushed down or clipped
    // by taller content above it — position is independent of line font sizes.
    position: 'absolute',
    left: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#fff'
  },
  qrCodeLandscape: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badgeCounter: {
    fontSize: ptToPx(10),
    fontWeight: 'bold',
    textAlign: 'center',
    fontFamily: 'helvetica, arial, sans-serif',
    // Kept explicit and fixed so it matches the counterHeightPx constant the vertical
    // budget calculation in the component body assumes.
    lineHeight: 1.15,
    margin: '0px'
  },
  orientationToggle: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center'
  }
}))
